import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NavAcciones from '../../components/NavAcciones';
import TimelineNode from './components/TimelineNode';
import WorkOrderSummary from './components/WorkOrderSummary';
import PendingActions from './components/PendingActions';
import TimelineFilters from './components/TimelineFilters';

const WorkOrderTimeline = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedWorkOrder, setSelectedWorkOrder] = useState('OT-000123');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedResponsible, setSelectedResponsible] = useState('');

  const mockWorkOrderData = {
    workOrderNumber: 'OT-000123',
    vehicleData: {
      type: 'Automóvil',
      plates: 'ABC-123-XY',
      brand: 'Toyota',
      model: 'Corolla',
      year: '2020',
      mileage: 45000,
      color: 'Plata',
      vin: '1HGBH41JXMN109186'
    },
    clientData: {
      name: 'Carlos Méndez García',
      phone: '+52 55 1234 5678',
      email: 'carlos.mendez@email.com',
      rfc: 'MEGC850615HDF',
      company: 'Transportes del Norte SA de CV'
    },
    financialSummary: {
      subtotal: 8500.00,
      tax: 1360.00,
      total: 9860.00
    },
    currentStatus: 'cotizando',
    createdDate: '15/02/2026',
    lastUpdate: '17/02/2026 14:30'
  };

  const mockTimelineData = [
  {
    stage: 'recepcion',
    status: 'completed',
    timestamp: '15/02/2026 09:15',
    responsiblePerson: 'María González - Recepción',
    findings: `Vehículo recibido en condiciones generales buenas.\n\nSe registraron los siguientes elementos:\n• Nivel de combustible: 3/4 tanque\n• Herramientas completas en cajuela\n• Gato hidráulico presente\n• Llanta de refacción en buen estado\n\nMotivo de ingreso: Mantenimiento preventivo y revisión de frenos por ruido al frenar.`,
    modifications: [],
    attachments: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_167c94957-1772616174727.png",
      alt: 'Silver Toyota Corolla sedan parked in workshop reception area with fluorescent lighting overhead',
      description: 'Vista frontal del vehículo'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_15dc57a6b-1772616172511.png",
      alt: 'Close-up of vehicle dashboard showing odometer reading 45000 kilometers with warning lights off',
      description: 'Tablero - Kilometraje'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0aec74f-1772616174731.png",
      alt: 'Interior view of car trunk showing complete tool kit, hydraulic jack, and spare tire in good condition',
      description: 'Cajuela - Herramientas'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_19de4516d-1772616181374.png",
      alt: 'Exterior rear view of silver sedan showing license plate ABC-123-XY and minor scratches on bumper',
      description: 'Vista trasera - Placas'
    }],

    communications: [
    {
      type: 'client',
      recipient: 'Carlos Méndez',
      timestamp: '15/02/2026 09:30',
      message: 'Vehículo recibido correctamente. Le enviaremos actualizaciones del proceso de inspección.'
    }]

  },
  {
    stage: 'inspeccion',
    status: 'completed',
    timestamp: '15/02/2026 11:45',
    responsiblePerson: 'Roberto Sánchez - Técnico Mecánico',
    findings: `INSPECCIÓN TÉCNICA COMPLETA\n\nFRENOS:\n• Pastillas delanteras: 40% de vida útil restante\n• Pastillas traseras: 60% de vida útil restante\n• Discos delanteros: Desgaste irregular, requieren rectificado\n• Discos traseros: Estado aceptable\n• Líquido de frenos: Nivel correcto, color oscuro (recomendar cambio)\n\nSUSPENSIÓN:\n• Amortiguadores delanteros: Presentan fuga leve de aceite\n• Amortiguadores traseros: Estado aceptable\n• Terminales de dirección: Holgura detectada en lado derecho\n• Rótulas: Estado aceptable\n\nAFINACIÓN:\n• Bujías: Desgaste normal para kilometraje\n• Filtro de aire: 70% de vida útil\n• Filtro de combustible: Recomendar cambio\n• Batería: 12.4V - Estado aceptable\n\nOTROS:\n• Llantas: Desgaste irregular en delanteras (80% vida útil)\n• Alineación y balanceo requeridos\n• Nivel de aceite motor: Correcto\n• Fugas: No detectadas`,
    modifications: [],
    attachments: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1793126c2-1772616175704.png",
      alt: 'Mechanic inspecting worn brake pads with 40 percent remaining thickness using digital caliper measurement tool',
      description: 'Pastillas de freno delanteras'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f009bf4b-1772616178479.png",
      alt: 'Close-up of front brake disc showing irregular wear pattern and scoring marks requiring resurfacing',
      description: 'Disco delantero con desgaste'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b1777886-1764709208544.png",
      alt: 'Leaking front shock absorber with visible oil residue on chrome shaft and dust boot',
      description: 'Amortiguador con fuga'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_17c2139d3-1772130840896.png",
      alt: 'Worn steering tie rod end showing excessive play and damaged rubber boot requiring replacement',
      description: 'Terminal de dirección con holgura'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_12987e217-1772616170459.png",
      alt: 'Front tire showing uneven tread wear pattern with 80 percent remaining depth measured with gauge',
      description: 'Desgaste irregular de llanta'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_19f9763f7-1771360617511.png",
      alt: 'Dark brown brake fluid in transparent reservoir indicating oxidation and need for replacement service',
      description: 'Líquido de frenos oscuro'
    }],

    communications: [
    {
      type: 'internal',
      recipient: 'Equipo de Ventas',
      timestamp: '15/02/2026 12:00',
      message: 'Inspección completada. Se detectaron varios puntos críticos que requieren atención inmediata. Enviando hallazgos para cotización.'
    }]

  },
  {
    stage: 'cotizacion',
    status: 'in-progress',
    timestamp: '16/02/2026 10:20',
    responsiblePerson: 'Laura Martínez - Ventas',
    findings: `COTIZACIÓN EN DESARROLLO\n\nServicios propuestos basados en inspección:\n\n1. Sistema de Frenos:\n   • Rectificado de discos delanteros\n   • Reemplazo de pastillas delanteras\n   • Cambio de líquido de frenos\n   • Mano de obra especializada\n\n2. Sistema de Suspensión:\n   • Reemplazo de amortiguadores delanteros (par)\n   • Reemplazo de terminal de dirección derecho\n   • Alineación y balanceo\n\n3. Mantenimiento Preventivo:\n   • Cambio de filtro de combustible\n   • Servicio de afinación menor\n\nEstado: Esperando cotizaciones de proveedores para refacciones.\nCliente contactado para autorización preliminar.`,
    modifications: [
    {
      field: 'Precio - Amortiguadores',
      oldValue: '$3,200.00',
      newValue: '$2,850.00',
      reason: 'Descuento por compra de par + instalación'
    },
    {
      field: 'Servicio adicional',
      oldValue: 'No incluido',
      newValue: 'Alineación y balanceo',
      reason: 'Recomendación técnica post-instalación de suspensión'
    }],

    attachments: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9b045db-1764781909449.png",
      alt: 'Computer screen displaying detailed quotation spreadsheet with line items, prices, and total calculations',
      description: 'Cotización en sistema'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_15adf9c83-1772616175433.png",
      alt: 'Printed quotation document on desk with itemized services, parts list, and customer signature line',
      description: 'Documento de cotización'
    }],

    communications: [
    {
      type: 'client',
      recipient: 'Carlos Méndez',
      timestamp: '16/02/2026 11:00',
      message: 'Hemos preparado una cotización detallada basada en la inspección. Los trabajos más urgentes son el sistema de frenos y la suspensión. ¿Podemos agendar una llamada para revisar los detalles?'
    },
    {
      type: 'client',
      recipient: 'Carlos Méndez',
      timestamp: '16/02/2026 15:30',
      message: 'Cliente solicita cotización por escrito vía email. Enviando documento con desglose completo y opciones de pago.'
    }]

  },
  {
    stage: 'proveedores',
    status: 'in-progress',
    timestamp: '17/02/2026 09:00',
    responsiblePerson: 'Laura Martínez - Ventas',
    findings: `SOLICITUD A PROVEEDORES\n\nRefacciones solicitadas:\n\n1. Amortiguadores delanteros KYB Gas-A-Just\n   • Cantidad: 2 piezas\n   • Aplicación: Toyota Corolla 2020\n   • Proveedores contactados: 3\n\n2. Terminal de dirección derecho\n   • Marca: Original o equivalente\n   • Proveedores contactados: 2\n\n3. Pastillas de freno delanteras\n   • Marca: Brembo o equivalente\n   • Proveedores contactados: 3\n\n4. Filtro de combustible\n   • OEM o equivalente de calidad\n   • Proveedores contactados: 2\n\nEstado actual:\n• 2 proveedores han respondido con cotizaciones\n• Esperando respuesta de 3 proveedores adicionales\n• Tiempo estimado de entrega: 2-3 días hábiles`,
    modifications: [],
    attachments: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1df74a97b-1772221194625.png",
      alt: 'WhatsApp group chat showing supplier quotations with part numbers, prices, and delivery times',
      description: 'Grupo de difusión - Respuestas'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_15e4e4ed7-1771360620300.png",
      alt: 'Spreadsheet comparing three supplier quotes with prices, delivery times, and warranty information',
      description: 'Comparativa de cotizaciones'
    }],

    communications: [
    {
      type: 'supplier',
      recipient: 'Grupo Proveedores',
      timestamp: '17/02/2026 09:15',
      message: 'Solicitud enviada a grupo de difusión con especificaciones técnicas y fotos de refacciones requeridas.'
    },
    {
      type: 'supplier',
      recipient: 'Autopartes del Centro',
      timestamp: '17/02/2026 11:30',
      message: 'Cotización recibida: Amortiguadores KYB $2,850 el par. Entrega 48hrs. Garantía 1 año.'
    },
    {
      type: 'supplier',
      recipient: 'Refacciones Martínez',
      timestamp: '17/02/2026 13:45',
      message: 'Cotización recibida: Pastillas Brembo $850. Terminal dirección $420. Disponibilidad inmediata.'
    }]

  }];


  const mockPendingActions = [
  {
    title: 'Autorización de Cliente Pendiente',
    description: 'El cliente Carlos Méndez debe revisar y autorizar la cotización de $9,860.00 MXN para proceder con los trabajos.',
    priority: 'high',
    assignedTo: 'Laura Martínez',
    dueDate: '18/02/2026',
    isOverdue: false,
    actionButton: 'Contactar Cliente',
    actionIcon: 'Phone'
  },
  {
    title: 'Cotizaciones de Proveedores Incompletas',
    description: 'Faltan 3 respuestas de proveedores para completar la comparativa de precios y seleccionar la mejor opción.',
    priority: 'medium',
    assignedTo: 'Laura Martínez',
    dueDate: '17/02/2026',
    isOverdue: false,
    actionButton: 'Revisar Respuestas',
    actionIcon: 'MessageSquare'
  },
  {
    title: 'Programar Fecha de Servicio',
    description: 'Una vez autorizada la cotización, coordinar con el cliente la fecha y hora para realizar los trabajos (estimado 2 días).',
    priority: 'low',
    assignedTo: 'María González',
    dueDate: '19/02/2026',
    isOverdue: false,
    actionButton: 'Ver Calendario',
    actionIcon: 'Calendar'
  }];


  const mockWorkOrderOptions = [
  { value: 'OT-000123', label: 'OT-000123 - Toyota Corolla 2020' },
  { value: 'OT-000122', label: 'OT-000122 - Honda Civic 2019' },
  { value: 'OT-000121', label: 'OT-000121 - Nissan Versa 2021' },
  { value: 'OT-000120', label: 'OT-000120 - Mazda 3 2018' },
  { value: 'OT-000119', label: 'OT-000119 - Volkswagen Jetta 2020' }];


  const mockResponsibleOptions = [
  { value: '', label: 'Todos los responsables' },
  { value: 'maria-gonzalez', label: 'María González - Recepción' },
  { value: 'roberto-sanchez', label: 'Roberto Sánchez - Técnico' },
  { value: 'laura-martinez', label: 'Laura Martínez - Ventas' },
  { value: 'juan-perez', label: 'Juan Pérez - Técnico' },
  { value: 'ana-lopez', label: 'Ana López - Ventas' }];


  const toggleNode = (stage) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [stage]: !prev?.[stage]
    }));
  };

  const handleActionClick = (action) => {
    console.log('Action clicked:', action);
  };

  const handleClearFilters = () => {
    setSelectedWorkOrder('OT-000123');
    setDateRange({ start: '', end: '' });
    setSelectedStage('');
    setSelectedResponsible('');
  };

  return (
    <>
      <Helmet>
        <title>Timeline de Orden de Trabajo - Gearcontrol</title>
        <meta
          name="description"
          content="Seguimiento cronológico completo de órdenes de trabajo con historial detallado de cada etapa del proceso de taller" />
        
      </Helmet>
      <div className="min-h-screen bg-background">
        <MainSidebar isCollapsed={isSidebarCollapsed} userRole="admin" />

        <div
          className={`
            transition-smooth
            ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
          `}>
          
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1">
            <div className="flex items-center justify-between gap-4 px-4 md:px-6 lg:px-8 py-4 md:py-6">
              <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 transition-smooth press-effect focus-ring flex-shrink-0"
                  aria-label="Toggle sidebar">

                  <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                </button>
                <div className="min-w-0 pl-10 lg:pl-0">
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground leading-tight">
                    Timeline de Orden de Trabajo
                  </h1>
                  <p className="text-sm md:text-base caption text-muted-foreground mt-1">
                    Seguimiento cronológico completo del proceso
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                className="hidden md:flex flex-shrink-0">
                
                Exportar PDF
              </Button>
            </div>
          </header>

          <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-6 md:space-y-8">
            <NavAcciones
              onRegresar={() => navigate(-1)}
              onSalir={() => navigate('/signin')}
              label="Línea de Tiempo de OT" />
            
            <TimelineFilters
              selectedWorkOrder={selectedWorkOrder}
              dateRange={dateRange}
              selectedStage={selectedStage}
              selectedResponsible={selectedResponsible}
              onWorkOrderChange={setSelectedWorkOrder}
              onDateRangeChange={setDateRange}
              onStageChange={setSelectedStage}
              onResponsibleChange={setSelectedResponsible}
              onClearFilters={handleClearFilters}
              workOrderOptions={mockWorkOrderOptions}
              responsibleOptions={mockResponsibleOptions} />
            

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon name="GitBranch" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                        Historial Cronológico
                      </h2>
                      <p className="text-sm caption text-muted-foreground">
                        {mockTimelineData?.length} etapas registradas
                      </p>
                    </div>
                  </div>

                  <div className="space-y-0">
                    {mockTimelineData?.map((node, index) =>
                    <TimelineNode
                      key={node?.stage}
                      stage={node?.stage}
                      status={node?.status}
                      timestamp={node?.timestamp}
                      responsiblePerson={node?.responsiblePerson}
                      findings={node?.findings}
                      modifications={node?.modifications}
                      attachments={node?.attachments}
                      communications={node?.communications}
                      isLast={index === mockTimelineData?.length - 1}
                      isExpanded={expandedNodes?.[node?.stage]}
                      onToggle={() => toggleNode(node?.stage)} />

                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border border-border p-4 md:p-6">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon name="Info" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                        Próximos Pasos
                      </h3>
                      <ul className="space-y-2 text-sm md:text-base font-body text-foreground">
                        <li className="flex items-start gap-2">
                          <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>Esperar autorización del cliente para proceder</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Clock" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                          <span>Completar cotizaciones de proveedores pendientes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Calendar" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                          <span>Programar fecha de servicio (estimado 2 días de trabajo)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6 md:space-y-8">
                <WorkOrderSummary
                  workOrderNumber={mockWorkOrderData?.workOrderNumber}
                  vehicleData={mockWorkOrderData?.vehicleData}
                  clientData={mockWorkOrderData?.clientData}
                  financialSummary={mockWorkOrderData?.financialSummary}
                  currentStatus={mockWorkOrderData?.currentStatus}
                  createdDate={mockWorkOrderData?.createdDate}
                  lastUpdate={mockWorkOrderData?.lastUpdate} />
                

                <PendingActions
                  actions={mockPendingActions}
                  onActionClick={handleActionClick} />
                
              </div>
            </div>
          </main>
        </div>
      </div>
    </>);

};

export default WorkOrderTimeline;